import { requireMfaStepUp } from "../auth/mfaStepUp";

export type ManagedSigningPurpose =
  | "collector_release_signer"
  | "tuf_targets_signer"
  | "tuf_snapshot_signer"
  | "tuf_timestamp_signer";

export type TufManagedSigningPurpose = Exclude<ManagedSigningPurpose, "collector_release_signer">;
export type GeneratableManagedSigningPurpose = ManagedSigningPurpose;
export type TufActivationChannel = "stable" | "beta";

export type ManagedSigningIdentity = {
  operation_id: string;
  key_record_id: string;
  purpose: ManagedSigningPurpose;
  family: string;
  lifecycle:
    | "provisioning"
    | "staged"
    | "abandoned"
    | "active"
    | "retired"
    | "revocation_pending"
    | "revoked";
  provider_kind: "protected_local" | "protected_local_p256";
  provider_reference: string;
  algorithm: "ed25519" | "ecdsa-p256-sha256";
  consumers: string[];
  public_key_b64: string | null;
  fingerprint_sha256: string | null;
  key_id: string | null;
  version: number;
  created_at: string;
  private_material_exposed: false;
};

export type EvidenceWrapIdentity = {
  operation_id: string;
  key_record_id: string;
  lifecycle: "provisioning" | "staged";
  provider_kind: string;
  provider_reference: string;
  algorithm: "rsa-oaep-sha256";
  public_key_spki_b64: string | null;
  fingerprint_sha256: string | null;
  key_id: string | null;
  key_size_bits: 4096;
  version: number;
  created_at: string;
  active_for_new_collections: boolean;
  previously_activated: boolean;
  private_material_exposed: false;
};

export type EvidenceWrapAuthority = {
  contract_version: "kavoosh-evidence-wrap-authority.v1";
  authority_id: string;
  key_record_id: string;
  key_version: number;
  algorithm: "rsa-oaep-sha256";
  public_key_spki_b64: string;
  fingerprint_sha256: string;
  key_size_bits: 4096;
  private_material_exposed: false;
};

export type TufActivationPreflight = {
  operation_id: string;
  purpose: TufManagedSigningPurpose;
  role: "targets" | "snapshot" | "timestamp";
  platform: "windows";
  channel: TufActivationChannel;
  key_id: string;
  root_version: number;
  threshold: number;
  lifecycle: "staged";
  private_material_exposed: false;
};

export type TufActivation = {
  operation_id: string;
  purpose: TufManagedSigningPurpose;
  role: "targets" | "snapshot" | "timestamp";
  platform: "windows";
  channel: TufActivationChannel;
  key_id: string;
  root_version: number;
  root_signed_sha256: string;
  threshold: number;
  lifecycle: "active";
  private_material_exposed: false;
};

export type TufInitialBootstrapResult = {
  contract_version: "kavoosh-keys-trust-tuf-bootstrap.v1";
  platform: "windows";
  channel: "beta";
  mode: "bootstrapped" | "publication_recovered" | "already_complete";
  root_version: number;
  root_key_id: string;
  public_health: "healthy" | "expiring";
  private_material_exposed: false;
};

export type TufMaintenancePolicy = {
  contract_version: "kavoosh-keys-trust-tuf-maintenance-policy.v1";
  platform: "windows";
  channel: "beta";
  policy_version: number;
  root_validity_days: number;
  targets_validity_days: number;
  snapshot_validity_days: number;
  timestamp_validity_days: number;
  warning_days: number;
  private_material_exposed: false;
};

export type TufMaintenancePolicyInput = Pick<
  TufMaintenancePolicy,
  | "root_validity_days"
  | "targets_validity_days"
  | "snapshot_validity_days"
  | "timestamp_validity_days"
  | "warning_days"
>;

export type TufMetadataMaintenanceResult = {
  contract_version: "kavoosh-keys-trust-tuf-maintenance.v1";
  status: "renewed" | "reconciled";
  previous_metadata_version: number;
  active_metadata_version: number;
  reason: "expired" | "expiring";
  affected_roles: Array<"targets" | "snapshot" | "timestamp">;
  preserved_target_sha256: string;
  targets_key_id: string;
  snapshot_key_id: string;
  timestamp_key_id: string;
  timestamp_commit_point: "PASS";
  public_health: "healthy" | "expiring";
  private_material_exposed: false;
};

export type RevocationDependency = {
  record_id: string;
  release_key: string;
  version: number;
  status: "draft" | "active" | "completed" | "failed" | "revoked" | "superseded";
};

export type ManagedSigningRevocationPreflight = {
  contract_version: "kavoosh-keys-trust-revocation-preflight.v1";
  operation_id: string;
  key_id: string;
  dependency_count: number;
  revocation_allowed: boolean;
  blocking_dependencies: RevocationDependency[];
};

export type ManagedSigningRevocationResult = {
  contract_version: "kavoosh-keys-trust-revocation.v1";
  identity: ManagedSigningIdentity;
  trust_removed: boolean;
  private_material_exposed: false;
};

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";
type ErrorBody = { detail?: unknown };
const HEX64 = /^[0-9a-f]{64}$/;
const TUF_ROLE_BY_PURPOSE: Record<TufManagedSigningPurpose, TufActivation["role"]> = {
  tuf_targets_signer: "targets",
  tuf_snapshot_signer: "snapshot",
  tuf_timestamp_signer: "timestamp",
};

function cookie(name: string): string | undefined {
  return document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

function errorCode(payload: ErrorBody): string | null {
  const value = payload.detail;
  if (!value || typeof value !== "object" || !("code" in value)) return null;
  return typeof value.code === "string" ? value.code : null;
}

function errorDetail(response: Response, payload: ErrorBody): string {
  const value = payload.detail;
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "message" in value) return String(value.message);
  if (response.status === 403) return "Platform Admin authority with recent MFA is required.";
  if (response.status === 409) return "This signing operation conflicts with existing lifecycle or provider compatibility state.";
  if (response.status === 503) return "Protected key custody is not available in this runtime.";
  return "Keys & Trust signing identity request could not be completed.";
}

async function request<T>(path: string, init: RequestInit = {}, allowStepUpRetry = true): Promise<T> {
  const csrf = cookie("kavoosh_csrf");
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.method && init.method !== "GET" && csrf
        ? { "X-CSRF-Token": decodeURIComponent(csrf) }
        : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as ErrorBody;
    if (response.status === 401) throw new Error("SESSION_UNAVAILABLE");
    if (response.status === 403 && errorCode(payload) === "MFA_STEP_UP_REQUIRED" && allowStepUpRetry) {
      await requireMfaStepUp();
      return request<T>(path, init, false);
    }
    throw new Error(errorDetail(response, payload));
  }
  return await response.json() as T;
}

function assertIdentity(value: ManagedSigningIdentity): ManagedSigningIdentity {
  if (value.private_material_exposed !== false) {
    throw new Error("Keys & Trust signing response failed its private-material safety contract.");
  }
  if (
    value.lifecycle !== "provisioning" &&
    (!value.fingerprint_sha256 || !value.key_id || !value.public_key_b64)
  ) {
    throw new Error("Keys & Trust signing identity is incomplete.");
  }
  if (
    value.purpose === "collector_release_signer" &&
    (value.provider_kind !== "protected_local" || value.algorithm !== "ed25519")
  ) {
    throw new Error("Collector Release signing identity has an incompatible custody provider.");
  }
  if (
    value.purpose !== "collector_release_signer" &&
    (value.provider_kind !== "protected_local_p256" || value.algorithm !== "ecdsa-p256-sha256")
  ) {
    throw new Error("TUF signing identity has an incompatible custody provider.");
  }
  return value;
}

function roleForPurpose(purpose: TufManagedSigningPurpose): TufActivation["role"] {
  return TUF_ROLE_BY_PURPOSE[purpose];
}

function assertTufActivationPreflight(
  value: TufActivationPreflight,
  operationId: string,
  channel: TufActivationChannel,
): TufActivationPreflight {
  if (
    value.operation_id !== operationId ||
    roleForPurpose(value.purpose) !== value.role ||
    value.platform !== "windows" ||
    value.channel !== channel ||
    !HEX64.test(value.key_id) ||
    !Number.isInteger(value.root_version) ||
    value.root_version < 1 ||
    !Number.isInteger(value.threshold) ||
    value.threshold < 1 ||
    value.lifecycle !== "staged" ||
    value.private_material_exposed !== false
  ) {
    throw new Error("Managed TUF activation preflight response failed its authority contract.");
  }
  return value;
}

function assertTufActivation(value: TufActivation): TufActivation {
  if (
    roleForPurpose(value.purpose) !== value.role ||
    value.platform !== "windows" ||
    !["stable", "beta"].includes(value.channel) ||
    !HEX64.test(value.key_id) ||
    !Number.isInteger(value.root_version) ||
    value.root_version < 1 ||
    !HEX64.test(value.root_signed_sha256) ||
    !Number.isInteger(value.threshold) ||
    value.threshold < 1 ||
    value.lifecycle !== "active" ||
    value.private_material_exposed !== false
  ) {
    throw new Error("Managed TUF activation response failed its authority contract.");
  }
  return value;
}

function assertTufInitialBootstrap(value: TufInitialBootstrapResult): TufInitialBootstrapResult {
  if (
    value.contract_version !== "kavoosh-keys-trust-tuf-bootstrap.v1"
    || value.platform !== "windows"
    || value.channel !== "beta"
    || !["bootstrapped", "publication_recovered", "already_complete"].includes(value.mode)
    || !Number.isInteger(value.root_version)
    || value.root_version < 1
    || !HEX64.test(value.root_key_id)
    || !["healthy", "expiring"].includes(value.public_health)
    || value.private_material_exposed !== false
  ) {
    throw new Error("Initial TUF bootstrap response failed its authority contract.");
  }
  return value;
}

function assertTufMaintenancePolicy(value: TufMaintenancePolicy): TufMaintenancePolicy {
  if (
    value.contract_version !== "kavoosh-keys-trust-tuf-maintenance-policy.v1"
    || value.platform !== "windows"
    || value.channel !== "beta"
    || !Number.isInteger(value.policy_version)
    || value.policy_version < 1
    || !Number.isInteger(value.root_validity_days)
    || !Number.isInteger(value.targets_validity_days)
    || !Number.isInteger(value.snapshot_validity_days)
    || !Number.isInteger(value.timestamp_validity_days)
    || !Number.isInteger(value.warning_days)
    || !(value.root_validity_days > value.targets_validity_days
      && value.targets_validity_days > value.snapshot_validity_days
      && value.snapshot_validity_days > value.timestamp_validity_days
      && value.timestamp_validity_days > value.warning_days)
    || value.private_material_exposed !== false
  ) {
    throw new Error("TUF maintenance policy response failed its authority contract.");
  }
  return value;
}

function assertTufMetadataMaintenance(
  value: TufMetadataMaintenanceResult,
): TufMetadataMaintenanceResult {
  if (
    value.contract_version !== "kavoosh-keys-trust-tuf-maintenance.v1"
    || !["renewed", "reconciled"].includes(value.status)
    || !Number.isInteger(value.previous_metadata_version)
    || value.previous_metadata_version < 1
    || !Number.isInteger(value.active_metadata_version)
    || value.active_metadata_version !== value.previous_metadata_version + 1
    || !["expired", "expiring"].includes(value.reason)
    || !Array.isArray(value.affected_roles)
    || value.affected_roles.length < 1
    || value.affected_roles.some((role) => !["targets", "snapshot", "timestamp"].includes(role))
    || !HEX64.test(value.preserved_target_sha256)
    || !HEX64.test(value.targets_key_id)
    || !HEX64.test(value.snapshot_key_id)
    || !HEX64.test(value.timestamp_key_id)
    || value.timestamp_commit_point !== "PASS"
    || !["healthy", "expiring"].includes(value.public_health)
    || value.private_material_exposed !== false
  ) {
    throw new Error("TUF maintenance response failed its authority contract.");
  }
  return value;
}

function assertRevocationPreflight(
  value: ManagedSigningRevocationPreflight,
  operationId: string,
): ManagedSigningRevocationPreflight {
  if (
    value.contract_version !== "kavoosh-keys-trust-revocation-preflight.v1" ||
    value.operation_id !== operationId ||
    typeof value.key_id !== "string" ||
    value.key_id.length !== 16 ||
    !Number.isInteger(value.dependency_count) ||
    value.dependency_count < 0 ||
    !Array.isArray(value.blocking_dependencies) ||
    value.blocking_dependencies.length !== value.dependency_count ||
    value.revocation_allowed !== (value.dependency_count === 0)
  ) {
    throw new Error("Keys & Trust revocation preflight response is invalid.");
  }
  return value;
}

function assertEvidenceWrapIdentity(value: EvidenceWrapIdentity): EvidenceWrapIdentity {
  if (
    value.private_material_exposed !== false
    || typeof value.provider_kind !== "string"
    || value.provider_kind.trim().length === 0
    || typeof value.provider_reference !== "string"
    || value.provider_reference.trim().length === 0
    || value.algorithm !== "rsa-oaep-sha256"
    || value.key_size_bits !== 4096
    || !["provisioning", "staged"].includes(value.lifecycle)
    || (value.lifecycle === "staged" && (
      !value.public_key_spki_b64
      || !value.fingerprint_sha256
      || !HEX64.test(value.fingerprint_sha256)
      || value.key_id !== value.fingerprint_sha256
    ))
  ) {
    throw new Error("Evidence wrapping identity failed its custody safety contract.");
  }
  return value;
}

function assertEvidenceWrapAuthority(value: EvidenceWrapAuthority): EvidenceWrapAuthority {
  if (
    value.contract_version !== "kavoosh-evidence-wrap-authority.v1"
    || value.private_material_exposed !== false
    || value.algorithm !== "rsa-oaep-sha256"
    || value.key_size_bits !== 4096
    || !Number.isInteger(value.key_version)
    || value.key_version < 1
    || !HEX64.test(value.fingerprint_sha256)
    || !value.public_key_spki_b64
    || value.authority_id !== `evidence-wrap:${value.key_record_id}`
  ) {
    throw new Error("Evidence wrapping authority failed its public authority contract.");
  }
  return value;
}

export async function listEvidenceWrapIdentities(): Promise<EvidenceWrapIdentity[]> {
  const values = await request<EvidenceWrapIdentity[]>("/admin/keys-trust/evidence-wrap-identities");
  if (!Array.isArray(values)) throw new Error("Evidence wrapping identity list is invalid.");
  return values.map(assertEvidenceWrapIdentity);
}

export async function stageEvidenceWrapIdentity(operationId: string): Promise<EvidenceWrapIdentity> {
  return assertEvidenceWrapIdentity(await request<EvidenceWrapIdentity>(
    "/admin/keys-trust/evidence-wrap-identities",
    {
      method: "POST",
      body: JSON.stringify({ operation_id: operationId }),
    },
  ));
}

export async function activateEvidenceWrapIdentity(operationId: string): Promise<EvidenceWrapAuthority> {
  return assertEvidenceWrapAuthority(await request<EvidenceWrapAuthority>(
    `/admin/keys-trust/evidence-wrap-identities/${operationId}/activation`,
    { method: "POST" },
  ));
}

export async function getEvidenceWrapAuthority(): Promise<EvidenceWrapAuthority> {
  return assertEvidenceWrapAuthority(await request<EvidenceWrapAuthority>(
    "/admin/keys-trust/evidence-wrap-authority",
  ));
}

export async function listManagedSigningIdentities(): Promise<ManagedSigningIdentity[]> {
  const values = await request<ManagedSigningIdentity[]>("/admin/keys-trust/signing-identities");
  if (!Array.isArray(values)) throw new Error("Keys & Trust signing identity list is invalid.");
  return values.map(assertIdentity);
}

export async function stageManagedSigningIdentity(input: {
  operationId: string;
  purpose: GeneratableManagedSigningPurpose;
}): Promise<ManagedSigningIdentity> {
  const value = await request<ManagedSigningIdentity>("/admin/keys-trust/signing-identities", {
    method: "POST",
    body: JSON.stringify({ operation_id: input.operationId, purpose: input.purpose }),
  });
  return assertIdentity(value);
}

export async function abandonDuplicateStagedTufIdentity(
  operationId: string,
): Promise<ManagedSigningIdentity> {
  const value = await request<ManagedSigningIdentity>(
    `/admin/keys-trust/signing-identities/${operationId}/staging-abandonment`,
    {
      method: "POST",
      body: JSON.stringify({
        reason: "Duplicate staged TUF role cleanup before initial trust bootstrap.",
      }),
    },
  );
  const identity = assertIdentity(value);
  if (identity.operation_id !== operationId || identity.lifecycle !== "abandoned") {
    throw new Error("Managed TUF duplicate cleanup response failed its lifecycle contract.");
  }
  return identity;
}

export async function getTufActivationPreflight(
  operationId: string,
  channel: TufActivationChannel,
): Promise<TufActivationPreflight> {
  const value = await request<TufActivationPreflight>(
    `/admin/keys-trust/signing-identities/${operationId}/tuf-activation-preflight?platform=windows&channel=${encodeURIComponent(channel)}`,
  );
  return assertTufActivationPreflight(value, operationId, channel);
}

export async function listTufActivations(): Promise<TufActivation[]> {
  const values = await request<TufActivation[]>("/admin/keys-trust/tuf-activations");
  if (!Array.isArray(values)) throw new Error("Managed TUF activation list is invalid.");
  return values.map(assertTufActivation);
}

export async function activateTufSigningIdentity(
  operationId: string,
  channel: TufActivationChannel,
): Promise<TufActivation> {
  const value = await request<TufActivation>(
    `/admin/keys-trust/signing-identities/${operationId}/tuf-activation?platform=windows&channel=${encodeURIComponent(channel)}`,
    { method: "POST" },
  );
  const activation = assertTufActivation(value);
  if (activation.operation_id !== operationId || activation.channel !== channel) {
    throw new Error("Managed TUF activation response does not match the requested identity or scope.");
  }
  return activation;
}

export async function bootstrapWindowsBetaTuf(): Promise<TufInitialBootstrapResult> {
  const value = await request<TufInitialBootstrapResult>(
    "/admin/keys-trust/tuf-bootstrap",
    { method: "POST" },
  );
  return assertTufInitialBootstrap(value);
}

export async function getTufMaintenancePolicy(): Promise<TufMaintenancePolicy | null> {
  const value = await request<TufMaintenancePolicy | null>(
    "/admin/keys-trust/tuf-maintenance-policy",
  );
  return value === null ? null : assertTufMaintenancePolicy(value);
}

export async function setTufMaintenancePolicy(
  input: TufMaintenancePolicyInput,
): Promise<TufMaintenancePolicy> {
  return assertTufMaintenancePolicy(await request<TufMaintenancePolicy>(
    "/admin/keys-trust/tuf-maintenance-policy",
    {
      method: "PUT",
      body: JSON.stringify(input),
    },
  ));
}

export async function renewWindowsBetaTufMetadata(): Promise<TufMetadataMaintenanceResult> {
  const value = await request<TufMetadataMaintenanceResult>(
    "/admin/keys-trust/tuf-maintenance",
    { method: "POST" },
  );
  return assertTufMetadataMaintenance(value);
}

export async function activateManagedSigningIdentity(operationId: string): Promise<ManagedSigningIdentity> {
  const value = await request<ManagedSigningIdentity>(
    `/admin/keys-trust/signing-identities/${operationId}/activation`,
    { method: "POST" },
  );
  return assertIdentity(value);
}

export async function retireManagedSigningIdentity(
  operationId: string,
  reason: string,
): Promise<ManagedSigningIdentity> {
  const value = await request<ManagedSigningIdentity>(
    `/admin/keys-trust/signing-identities/${operationId}/retirement`,
    {
      method: "POST",
      body: JSON.stringify({ reason: reason.trim() }),
    },
  );
  return assertIdentity(value);
}

export async function getManagedSigningRevocationPreflight(
  operationId: string,
): Promise<ManagedSigningRevocationPreflight> {
  const value = await request<ManagedSigningRevocationPreflight>(
    `/admin/keys-trust/signing-identities/${operationId}/revocation-preflight`,
  );
  return assertRevocationPreflight(value, operationId);
}

export async function revokeManagedSigningIdentity(
  operationId: string,
  reason: string,
): Promise<ManagedSigningRevocationResult> {
  const value = await request<ManagedSigningRevocationResult>(
    `/admin/keys-trust/signing-identities/${operationId}/revocation`,
    {
      method: "POST",
      body: JSON.stringify({ reason: reason.trim() }),
    },
  );
  if (
    value.contract_version !== "kavoosh-keys-trust-revocation.v1" ||
    value.private_material_exposed !== false ||
    value.identity.operation_id !== operationId ||
    value.identity.lifecycle !== "revoked"
  ) {
    throw new Error("Keys & Trust revocation response failed its safety contract.");
  }
  return { ...value, identity: assertIdentity(value.identity) };
}